// Revenue / Margin roster cells: the same sparkline + reading treatment as the focus-ratio column,
// minus the benchmark (a revenue level has no industry anchor, and margin's belongs on the Ratios
// page where its band is explained).
//
// Headline values deliberately come from the caller's existing scalar rather than the series' last
// point. The scalars are what the roster has always shown; recomputing them from the trend would
// silently move numbers a firm has been reading for months.

import React from 'react'
import { Sparkline } from './charts'

const SPARK_W = 60
const SPARK_H = 22

export type CockpitMetricTrendCellProps = {
  /** Formatted headline, e.g. "$161K" or "53.9%". */
  display: string
  /** Oldest-first series for the sparkline. Fewer than 2 finite points renders the value alone. */
  values: number[]
  /** Tooltip labels aligned to `values`. */
  labels?: string[]
  /** Tooltip number formatter. */
  formatValue?: (value: number) => string
  /** Signed change vs the prior month, already in display units. Null hides the pill. */
  delta?: number | null
  /** Pre-formatted delta text, e.g. "31.8%" or "1.4 pts". */
  deltaDisplay?: string | null
  /** Whether a rise is good. `null` renders the pill untoned. */
  higherIsBetter?: boolean | null
  ariaLabel: string
}

/** Tone for a delta pill: polarity-aware, never raw sign. Mirrors the focus-ratio cell. */
export function metricDeltaTone(
  delta: number | null | undefined,
  higherIsBetter: boolean | null | undefined,
): 'up' | 'down' | 'neutral' {
  if (delta == null || !Number.isFinite(delta) || delta === 0) return 'neutral'
  if (higherIsBetter == null) return 'neutral'
  const good = higherIsBetter ? delta > 0 : delta < 0
  return good ? 'up' : 'down'
}

export function CockpitMetricTrendCell({
  display,
  values,
  labels,
  formatValue,
  delta,
  deltaDisplay,
  higherIsBetter = true,
  ariaLabel,
}: CockpitMetricTrendCellProps) {
  const finite = values.filter((v) => v != null && Number.isFinite(v))
  const tone = metricDeltaTone(delta, higherIsBetter)

  return (
    <div className="r2-metric">
      {finite.length >= 2 ? (
        <Sparkline
          className="r2-metric-spark"
          values={finite}
          down={tone === 'down'}
          neutral={tone === 'neutral'}
          endDot
          interactive
          labels={labels}
          formatValue={formatValue}
          width={SPARK_W}
          height={SPARK_H}
          ariaLabel={ariaLabel}
        />
      ) : (
        <span className="r2-metric-spark-gap" aria-hidden="true" />
      )}
      <div className="r2-metric-read">
        <span className="r2-metric-val">{display}</span>
        {delta != null && Number.isFinite(delta) && delta !== 0 && deltaDisplay ? (
          <span className={`delta-pill ${tone}`}>
            {delta > 0 ? '▲' : '▼'} {deltaDisplay}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default CockpitMetricTrendCell
